import { useLiveDraft } from "./useLiveDraft";
import { Button } from "../ui/Button";
import { Field, FieldNote, Form, NumInput, Select } from "../ui/Field";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { useContext } from "react";
import { I18nField, type I18nText } from "./I18nField";
import { BCoins, BScaleContext, scaleCoins } from "./BCoins";
import { GNumInput } from "./GNumInput";
import { CardRefSelect } from "./CardRefSelect";
import { fmt, pct, targetC, type Globals, type Level } from "../../lib/model";
import { ratio } from "../../lib/big";
import { useT } from "../../lib/i18n";
import { RANDOM_ITEM_REF,
  ACT_ITEMS, NEEDS_REF, REWARD_LABEL, frameOptions, qtyOf, refLabel, isStaleCardRef,
  type Reward, type RewardItem, type RewardKind,
} from "../../lib/reward";

const KINDS: RewardKind[] = ["money", "spin", "itemCard", "idolCard", "puzzle", "actItem"];

/**
 * 单关奖励编辑。
 *
 * 物品卡 / idol 卡 / 活动道具可以发多条（不同卡、不同主题），所以这三类是
 * 「可增删的列表」；money / spin / 拼图碎片各只有一个数量。
 */
export function RewardDialog({
  reward, level, bAt, globals, editable, itemCards, idolCards, onClose, onSave, title, hideName, allowRandomItem = false,
}: {
  reward: Reward;
  level?: Level;
  /** 这份奖励属于哪一关（萌宠小镇：哪一颗星）：money 的金币折算只看它；不传 = 不分关（每日奖励等），括号里给各关区间 */
  bAt?: number;
  /** 本版本的 E / ρ；没填实际总造价的关按它算目标造价 */
  globals?: Globals;
  editable: boolean;
  itemCards: string[];
  idolCards: string[];
  onClose: () => void;
  onSave: (r: Reward) => void;
  /** 复用方（每日奖励）自带标题；缺省是「第 N 关 · 通关奖励」 */
  title?: string;
  /** 每日奖励的档位没有名字，不显示名称行 */
  hideName?: boolean;
  /** 物品卡下拉多一项「随机物品」（全部物品卡按权重抽一张，同卡包集齐奖励）。通关奖励开（host 2026-10-26），其余复用方不开 */
  allowRandomItem?: boolean;
}) {
  // 边改边存：每一笔停手后交给 App，随版本自动保存；「完成」只是关掉
  const { draft, setDraft } = useLiveDraft<Reward>(reward, onSave, { enabled: editable });
  const t = useT();
  const goal = level ? targetC(level, globals) : null;
  const money = qtyOf(draft, "money");
  // money 以 B 计：发奖时 × 这一关运行时的 B（服务端 main-rewards.ts）。拿 B 的个数直接除金币造价，比例会小 B 倍
  const scale = useContext(BScaleContext);
  const bHere = bAt == null ? null : scale.rows.find((r) => r.n === bAt)?.b ?? null;
  const moneyCoins = money != null && bHere != null ? scaleCoins(bHere, money, scale.den) : null;
  const moneyShare = moneyCoins != null && goal ? ratio(moneyCoins, goal) : null;

  const setItems = (items: RewardItem[]) => setDraft({ ...draft, items });
  const simple = (k: RewardKind) => draft.items.find((i) => i.kind === k);
  const multi = (k: RewardKind) => draft.items.filter((i) => i.kind === k);

  const setSimple = (k: RewardKind, qty: number | null) => {
    const rest = draft.items.filter((i) => i.kind !== k);
    setItems(qty == null ? rest : [...rest, { kind: k, qty }]);
  };

  const options = (k: RewardKind) => (k === "itemCard" ? (allowRandomItem ? [RANDOM_ITEM_REF, ...itemCards] : itemCards) : k === "idolCard" ? idolCards : k === "frame" ? frameOptions() : ACT_ITEMS);

  return (
    <Modal
      title={title ?? t("dlg.rewardTitle", { n: draft.levelId })}
      width={640}
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={onClose}>{t("common.done")}</Button>
        </>
      }
    >
      <Form>
        {!hideName && <Field label={t("dlg.rewardName")}>
          <I18nField
            value={{ en: draft.name, ...draft.i18n } as I18nText}
            width={320}
            disabled={!editable}
            onChange={(v) => {
              const { en, ...rest } = v;
              setDraft({ ...draft, name: en ?? "", i18n: rest });
            }}
          />
        </Field>}

        {KINDS.map((k) =>
          NEEDS_REF.includes(k) ? (
            <Field key={k} label={t(REWARD_LABEL[k])}>
              {/* 可增删的多条：条目之间贴紧（gap-1.5），整块与标签的关系由 Field 管 */}
              <div className="flex w-full flex-col items-start gap-1.5">
                {multi(k).map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {k === "itemCard" || k === "idolCard" ? (
                      <CardRefSelect kind={k} value={it.ref ?? null} options={options(k)} disabled={!editable}
                        onChange={(v) => {
                          const all = draft.items.slice();
                          all[all.indexOf(it)] = { ...it, ref: v || null };
                          setItems(all);
                        }} />
                    ) : (
                    <Select
                      value={it.ref ?? ""}
                      disabled={!editable}
                      // 失效的卡牌引用：显示名可能和正确那项一样，红框 + 标记才看得出要重选
                      className={`w-[200px]${it.ref && isStaleCardRef(k, it.ref) ? " border-danger text-danger" : ""}`}
                      onChange={(v) => {
                        const all = draft.items.slice();
                        all[all.indexOf(it)] = { ...it, ref: v || null };
                        setItems(all);
                      }}
                    >
                      <option value="" disabled hidden>{t("dlg.select")}</option>
                      {options(k).map((o) => <option key={o} value={o}>{refLabel(k, o, t)}</option>)}
                      {it.ref && !options(k).includes(it.ref) && <option value={it.ref}>{refLabel(k, it.ref, t)}</option>}
                    </Select>
                    )}
                    <NumInput
                      value={it.qty}
                      disabled={!editable}
                      className="w-[100px]"
                      placeholder={t("dlg.qty")}
                      onChange={(v) => {
                        const all = draft.items.slice();
                        all[all.indexOf(it)] = { ...it, qty: v };
                        setItems(all);
                      }}
                    />
                    {editable && (
                      <Button size="sm" tone="danger" onClick={() => setItems(draft.items.filter((x) => x !== it))}>{t("dlg.remove")}</Button>
                    )}
                  </div>
                ))}
                {multi(k).length === 0 && <span className="text-sm text-ink-placeholder">{t("common.none")}</span>}
                {editable && (
                  <Button size="sm" onClick={() => setItems([...draft.items, { kind: k, ref: null, qty: null }])}>
                    {t("dlg.addKind", { kind: t(REWARD_LABEL[k]) })}
                  </Button>
                )}
              </div>
            </Field>
          ) : (
            <Field key={k} label={t(REWARD_LABEL[k])}>
              {k === "money" && scale.model === 2 ? (
                // 单掷金币版本：按「几倍单掷金币」填，存的仍是 B 数（换算用旧 E，见 GNumInput）
                <><GNumInput valueB={simple(k)?.qty ?? null} e={scale.e} disabled={!editable} className="w-[160px]" onChangeB={(v) => setSimple(k, v)} />
                  <BCoins qty={simple(k)?.qty ?? null} at={bAt} /></>
              ) : <>
              <NumInput
                value={simple(k)?.qty ?? null}
                disabled={!editable}
                className="w-[160px]"
                placeholder={t("common.none")}
                onChange={(v) => setSimple(k, v)}
              />
              {k === "money" && <><span className="text-sm text-ink-regular">{t("u.B")}</span><BCoins qty={simple(k)?.qty ?? null} at={bAt} /></>}
              </>}
            </Field>
          ),
        )}

        {moneyCoins != null && moneyShare != null ? (
          <FieldNote>
            <Notice tone={moneyShare > 0.3 ? "warning" : "plain"}>
              {t("dlg.moneyShare", { v: fmt(moneyCoins), p: pct(moneyShare, 1) })}
              {moneyShare > 0.3 && t("dlg.moneyHigh")}
            </Notice>
          </FieldNote>
        ) : null}
      </Form>
    </Modal>
  );
}
